import { Component, Input, NgZone, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApiRoutes } from '@sb/library/api';
import { CancellableObservable, DecodeHTML, DesignBlockImageScope, DesignBlockScope, Errors, FormBase, IFrameUserActionSubmitMessage, WebsitePageScope } from '@sb/library/base';
import { ContactFormInputItem, ErrorCode, VerifyEmailInputItem, WebsiteBlockContactFormDisplayItem, WebsiteBlockContactFormType } from '@sb/library/data-models';
import { RecaptchaComponent } from '@sb/library/recaptcha';
import { forkJoin } from 'rxjs';
import { DesignBlockIframeBaseViewComponent } from '../design-block-iframe-base-view.component';
import { ContactFormCustomFieldsComponent } from './custom-fields/contact-form-custom-fields.component';

class ContactFormForm extends FormBase {
}

@Component({
    selector: 'lib-contact-form-design-block',
    templateUrl: './contact-form-design-block.component.html',
    styleUrls: ['./contact-form-design-block.component.scss']
})
export class ContactFormDesignBlockComponent extends DesignBlockIframeBaseViewComponent implements DesignBlockScope, DesignBlockImageScope {

    override Scope = this.SetScope({
        Implements: [DesignBlockScope, DesignBlockImageScope],
        Requires: WebsitePageScope
    });

    @ViewChild('recaptcha') recaptchaRef!: RecaptchaComponent;
    @ViewChild(ContactFormCustomFieldsComponent, {static: false}) customFields!: ContactFormCustomFieldsComponent;

    @Input() websiteBlockData!: WebsiteBlockContactFormDisplayItem;

    readonly form = ContactFormForm.Create(false);

    protected captchaErrors: Errors = new Errors();
    protected captchaNeededErrors: Errors = new Errors();

    private _sendingMessage = false;
    private _showThankYouMessage = false;

    constructor(private _NgZone: NgZone) {
        super();
    }

    override ngOnInit() {
        super.ngOnInit();
        this.form.isViewOnly = this.isViewOnly;
    }

    //==== recaptcha
    SendMessageButton(): void {
        // On iOS, initial click on the button (without interacting with a form field) starts outside the ngZone
        this._NgZone.run(() => {
            this.recaptchaRef.GetCaptcha(this.Scope.renderContext.ContactFormRenderContext?.CaptchaRequired!);
        });
    }

    SendMessage(): void {
        this.form.CheckValidity().subscribe(result => {
            if (result) {
                this._sendingMessage = true;

                const emailControlsToVerify = this.customFields.GetEmailControls().filter(c => c.value?.trim() );

                if (emailControlsToVerify.length > 0) {
                    this.VerifyEmailsAndSubmit(emailControlsToVerify);
                } else {
                    this.Submit()
                }
            }
        });
    }

    CaptchaError() {
        this.form.Enable();
    }

    private VerifyEmailsAndSubmit(emailControlsToVerify: FormControl[]): void {
        const calls: CancellableObservable<boolean>[] = [];

        for (let control of emailControlsToVerify) {
            calls.push(ApiRoutes.Messaging.VerifyEmail.Call({
                Body: new VerifyEmailInputItem({
                    EmailAddress: control.value
                })
            }))
        }

        forkJoin(calls).subscribe((results: boolean[]) => {

            const isAllVerifiedEmails = results.every(result => result );

            if (isAllVerifiedEmails) {
                this.Submit();
            } else {
                this.form.Enable();
                this._sendingMessage = false;
                for (let i = 0; i < results.length; i++ ) {
                    if (!results[i]) {
                        emailControlsToVerify[i].SetApiError();
                    }
                }
            }
        });
    }

    private Submit(): void {
        const inputItem = new ContactFormInputItem({
            SchoolID: this.websiteBlockData.SchoolID,
            WebsiteBlockID: this.websiteBlockData.ID,
            Captcha: this.recaptchaRef.resolvedRecaptchaToken
        });

        inputItem.CustomFieldValues = this.customFields.GetCustomFieldValues();

        //call API to submit form
        ApiRoutes.Website.Contact.Call({
            Body: inputItem
        }).subscribe({
            next: _ => {
                this._showThankYouMessage = true;
                this.RunTrackingScriptFormComplete();
            },
            error: (error: typeof ApiRoutes.Website.Contact.Error) => {
                if (error.Status == 429) {
                    this.OpenDialog429ApiError().subscribe(_ => {
                        this._sendingMessage = false;
                    });
                } else if (error.Response.ErrorCode === ErrorCode.Captcha) {
                    this.recaptchaRef.ResetCaptchaToken();
                    this.Scope.renderContext.ContactFormRenderContext!.CaptchaRequired = true;
                    this.recaptchaRef.GetCaptcha(true);
                    this._sendingMessage = false;
                } else {
                    throw error;
                }
            }
        });
    }

    private RunTrackingScriptFormComplete(): void {
        this.parentIframe?.sendMessage(new IFrameUserActionSubmitMessage({
            FormTitle: DecodeHTML(this.websiteBlockData.Header!),
            FormType: 'contact'
        }));
    }

    get kind(): WebsiteBlockContactFormType {
        return this.websiteBlockData.ContactFormType;
    }

    get sendingMessage(): boolean {
        return this._sendingMessage;
    }

    get WebsiteBlockContactFormType(): typeof WebsiteBlockContactFormType {
        return WebsiteBlockContactFormType;
    }

    get showThankYouMessage() {
        return this._showThankYouMessage;
    }

    get imageData() {
        return this.websiteBlockData.Images[0];
    }
}
